import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { t } from '../i18n';
import { toast } from 'sonner';
import { Save, KeyRound, Sliders, AlertCircle } from 'lucide-react';

export default function ModelRoutingPage() {
  const { lang } = useAuth();
  const [s, setS] = useState(null);
  const [keys, setKeys] = useState({ openai: '', anthropic: '', gemini: '' });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/settings`).then(r => setS(r.data));
  useEffect(() => { load(); }, []);

  if (!s) return <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="skeleton h-24 rounded-sm" />)}</div>;

  const toggleModel = (k) => {
    setS(prev => ({ ...prev, models_enabled: { ...prev.models_enabled, [k]: !prev.models_enabled[k] } }));
    setDirty(true);
  };

  const setPolicy = (p) => { setS(prev => ({ ...prev, escalation_policy: p })); setDirty(true); };
  const setThresh = (v) => { setS(prev => ({ ...prev, confidence_threshold: v })); setDirty(true); };
  const setCap = (dept, v) => {
    setS(prev => ({ ...prev, monthly_caps: { ...prev.monthly_caps, [dept]: Number(v) } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        models_enabled: s.models_enabled,
        confidence_threshold: s.confidence_threshold,
        escalation_policy: s.escalation_policy,
        monthly_caps: s.monthly_caps,
      };
      const nonEmptyKeys = Object.fromEntries(
        Object.entries(keys).filter(([, v]) => v && v.trim())
      );
      if (Object.keys(nonEmptyKeys).length > 0) payload.api_keys = nonEmptyKeys;
      await axios.put(`${API}/settings`, payload);
      setKeys({ openai: '', anthropic: '', gemini: '' });
      setDirty(false);
      toast.success('Settings saved');
      load();
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up max-w-4xl" data-testid="model-routing-page">
      <div className="data-label accent-line text-[#DDA079] mb-3">{t(lang, 'nav.admin_routing')}</div>
      <h1 className="font-headings text-4xl text-white mb-2">Model Routing & API Keys</h1>
      <p className="text-slate-400 mb-8">Control which paid AI tiers are reachable and at what aggressiveness. Provide your own provider keys to override the platform default key.</p>

      {/* Model toggles */}
      <div className="v-card p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Sliders size={14} className="text-[#C17F59]" />
          <span className="data-label">Cost ladder · enable / disable tiers</span>
        </div>
        <div className="space-y-3">
          {Object.entries(s.model_catalog).map(([key, cfg]) => {
            const enabled = s.models_enabled[key];
            return (
              <div key={key} data-testid={`model-row-${key}`} className="flex items-center justify-between border border-white/5 p-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white text-[14px]">{cfg.label}</span>
                    <span className="v-badge cost">{cfg.tier}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono mt-1">{cfg.provider} · {cfg.model} · ₹{cfg.inr_per_query.toFixed(2)}/query</div>
                </div>
                <button
                  data-testid={`toggle-${key}`}
                  onClick={() => toggleModel(key)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-[#C17F59]' : 'bg-white/10'}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${enabled ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Escalation policy + threshold */}
      <div className="v-card p-6 mb-6">
        <div className="data-label mb-4">Escalation policy</div>
        <div className="grid grid-cols-3 gap-2 mb-6">
          {[
            { v: 'aggressive', label: 'Aggressive', desc: 'Always pick cheapest tier first' },
            { v: 'balanced', label: 'Balanced', desc: 'Default for most queries' },
            { v: 'conservative', label: 'Conservative', desc: 'Skip to mid-tier for safer answers' },
          ].map(p => (
            <button
              key={p.v}
              data-testid={`policy-${p.v}`}
              onClick={() => setPolicy(p.v)}
              className={`p-3 border text-left transition-colors ${s.escalation_policy === p.v ? 'border-[#C17F59] bg-[#C17F59]/[0.06]' : 'border-white/10 hover:border-white/25'}`}
            >
              <div className="text-white text-[13px]">{p.label}</div>
              <div className="text-[11px] text-slate-500 mt-1">{p.desc}</div>
            </button>
          ))}
        </div>
        <div className="data-label mb-2">Confidence threshold ({s.confidence_threshold.toFixed(2)})</div>
        <input
          data-testid="threshold-slider"
          type="range"
          min="0.5" max="0.95" step="0.01"
          value={s.confidence_threshold}
          onChange={e => setThresh(parseFloat(e.target.value))}
          className="w-full accent-[#C17F59]"
        />
        <div className="text-[11px] text-slate-500 mt-1">Below this confidence, the system escalates to the next-tier model.</div>
      </div>

      {/* Spending caps */}
      <div className="v-card p-6 mb-6">
        <div className="data-label mb-4">Monthly spending caps (INR per department)</div>
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(s.monthly_caps).map(([dept, v]) => (
            <div key={dept}>
              <label className="text-[12px] text-slate-300">{dept}</label>
              <input
                data-testid={`cap-${dept}`}
                type="number"
                value={v}
                onChange={e => setCap(dept, e.target.value)}
                className="v-input mt-1"
              />
            </div>
          ))}
        </div>
      </div>

      {/* API keys */}
      <div className="v-card p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={14} className="text-[#C17F59]" />
          <span className="data-label">Your provider API keys (overrides platform default key)</span>
        </div>
        <div className="flex items-start gap-2 mb-4 text-[12px] text-amber-200 bg-amber-500/[0.06] border border-amber-500/20 p-3">
          <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
          <span>Leave a field blank to continue using the platform default key. Existing keys are masked.</span>
        </div>
        <div className="space-y-4">
          {[
            { k: 'openai', label: 'OpenAI', placeholder: 'sk-…', hint: 'Used for GPT-4 Mini route' },
            { k: 'anthropic', label: 'Anthropic', placeholder: 'sk-ant-…', hint: 'Used for Claude Haiku & Sonnet routes' },
            { k: 'gemini', label: 'Google Gemini', placeholder: 'AIza…', hint: 'Used for Gemini Flash route' },
          ].map(p => (
            <div key={p.k}>
              <div className="flex justify-between text-[12px] mb-1">
                <span className="text-slate-300">{p.label}</span>
                <span className="font-mono text-slate-500">
                  current: {s.api_keys[p.k] ? <span className="text-emerald-400">{s.api_keys[p.k]} (custom)</span> : <span className="text-[#DDA079]">Platform default</span>}
                </span>
              </div>
              <input
                data-testid={`key-${p.k}`}
                type="password"
                value={keys[p.k]}
                onChange={e => { setKeys(k => ({ ...k, [p.k]: e.target.value })); setDirty(true); }}
                placeholder={p.placeholder}
                className="v-input"
              />
              <div className="text-[10px] text-slate-500 mt-1">{p.hint}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="sticky bottom-4 v-card p-4 flex items-center justify-between bg-[#121215]/95 backdrop-blur">
        <div className="text-[12px] text-slate-400">
          {dirty ? <span className="text-[#DDA079]">Unsaved changes</span> : <span>All settings saved</span>}
        </div>
        <button
          data-testid="save-settings"
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary flex items-center gap-2"
        >
          <Save size={13} />
          {saving ? 'Saving…' : t(lang, 'common.saveChanges')}
        </button>
      </div>
    </div>
  );
}
